"use client";

import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERIES } from "@/config/queries";
import { userProfileCompleted } from "@/providers/AuthProvider";
import { User } from "@/types/users/user";
import { UserWithoutTeachers } from "@/types/users/userWithoutTeachers";
import { Classification } from "@/types/classification";
import { updateUserProfile } from "@/actions/users/user";
import { InputWrapper } from "@/components/forms/InputWrapper";
import { TextInput } from "@/components/forms/TextInput";
import { ClassificationSelect } from "@/components/forms/ClassificationSelect";
import { Dropdown } from "@/components/forms/Dropdown";
import { ImageInput } from "@/components/forms/ImageInput";
import ProfileEdit from "@/components/profile/ProfileEdit";
import { USER_PROFILE_IMAGE_PROPS } from "./UserProfileHeader";

interface UserProfileEditProps {
    user: User;
    onClose: () => void;
}

export default function UserProfileEdit({
    user,
    onClose,
}: UserProfileEditProps) {
    const queryClient = useQueryClient();

    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);

    const { data: classes } = useQuery({
        ...QUERIES.classes.query,
        enabled: isMounted && !!user,
    });

    const { data: goals } = useQuery({
        ...QUERIES.teacher_goals.query,
        enabled: isMounted && !!user,
    });

    const { data: subjects } = useQuery({
        ...QUERIES.subjects.query,
        enabled: isMounted && !!user,
    });

    const [formData, setFormData] = useState<Partial<UserWithoutTeachers>>(
        user || {}
    );

    const onSingleValueClassificationSelect = (
        key: keyof UserWithoutTeachers,
        classification: Classification,
        selected: boolean
    ) => {
        setFormData((prev) => ({
            ...prev,
            [key]: selected ? classification : null,
        }));
    };

    const onMultipleValuesClassificationSelect = (
        key: keyof UserWithoutTeachers,
        classification: Classification,
        selected: boolean
    ) => {
        setFormData((prev) => ({
            ...prev,
            [key]: [
                ...((prev[key] as Classification[]) || []).filter(
                    (prevClassification) =>
                        prevClassification.id !== classification.id
                ),
                ...(selected ? [classification] : []),
            ],
        }));
    };

    const userChanges = useMemo(() => {
        return Object.keys(formData).filter(
            (key) =>
                formData[key as keyof UserWithoutTeachers] !==
                user?.[key as keyof UserWithoutTeachers]
        );
    }, [formData, user]);

    const hasChanges = useMemo(() => {
        return userChanges.length > 0;
    }, [userChanges]);

    const isValid = useMemo(() => userProfileCompleted(formData), [formData]);

    const { mutateAsync: updateUserProfileMutation } = useMutation({
        mutationFn: updateUserProfile,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: QUERIES.currentUser.query.queryKey,
            });
            await queryClient.invalidateQueries({
                queryKey: QUERIES.user.query(user.id).queryKey,
            });
            await queryClient.invalidateQueries({
                queryKey: QUERIES.users.query.queryKey,
            });
        },
    });

    const onSave = async () => {
        await updateUserProfileMutation(formData);
        onClose();
    };

    const saveDisabled = useMemo(() => {
        return !hasChanges || !isValid;
    }, [hasChanges, isValid]);

    useEffect(() => {
        setFormData(user || {});
    }, [user]);

    const FirstColumnContent = (
        <>
            <InputWrapper label="תמונת פרופיל">
                <ImageInput
                    currentFile={formData.image}
                    onChange={(file) =>
                        setFormData({
                            ...formData,
                            image: file,
                        })
                    }
                    {...USER_PROFILE_IMAGE_PROPS}
                />
            </InputWrapper>

            {[
                {
                    accessor: "first_name" as keyof UserWithoutTeachers,
                    label: "שם פרטי",
                },
                {
                    accessor: "last_name" as keyof UserWithoutTeachers,
                    label: "שם משפחה",
                },
            ].map((field) => (
                <InputWrapper key={field.accessor} label={field.label}>
                    <TextInput
                        value={(formData[field.accessor] as string) || ""}
                        placeholder={`הזן ${field.label}`}
                        onChange={(value) =>
                            setFormData({
                                ...formData,
                                [field.accessor]: value,
                            })
                        }
                        required={true}
                    />
                </InputWrapper>
            ))}

            <InputWrapper label="מקצוע לימוד עיקרי">
                <Dropdown
                    items={subjects || []}
                    selectedItems={
                        formData.main_subject ? [formData.main_subject] : []
                    }
                    placeholder="בחר מקצוע לימוד עיקרי"
                    onSelect={(classification, selected) => {
                        onSingleValueClassificationSelect(
                            "main_subject",
                            classification as Classification,
                            selected
                        );
                        onMultipleValuesClassificationSelect(
                            "additional_subjects",
                            classification as Classification,
                            false
                        );
                    }}
                />
            </InputWrapper>

            <InputWrapper label="מקצועות לימוד נוספים">
                <Dropdown
                    items={
                        subjects?.filter(
                            (subject) =>
                                subject.id !== formData.main_subject?.id
                        ) || []
                    }
                    selectedItems={formData.additional_subjects || []}
                    isMultiSelect={true}
                    placeholder="בחר מקצועות לימוד נוספים"
                    onSelect={(classification, selected) =>
                        onMultipleValuesClassificationSelect(
                            "additional_subjects",
                            classification as Classification,
                            selected
                        )
                    }
                />
            </InputWrapper>
        </>
    );
    const SecondColumnContent = (
        <>
            <InputWrapper label="שכבות לימוד">
                <ClassificationSelect
                    variant="small"
                    classifications={classes || []}
                    selectedClassifications={formData.classes || []}
                    onSelect={(classification, selected) =>
                        onMultipleValuesClassificationSelect(
                            "classes",
                            classification,
                            selected
                        )
                    }
                />
            </InputWrapper>

            <InputWrapper label="מה הכי חשוב לך לקדם בתלמידים שלך?">
                <ClassificationSelect
                    variant="large"
                    classifications={goals || []}
                    selectedClassifications={
                        formData.main_goal ? [formData.main_goal] : []
                    }
                    onSelect={(classification, selected) =>
                        onSingleValueClassificationSelect(
                            "main_goal",
                            classification,
                            selected
                        )
                    }
                />
            </InputWrapper>
        </>
    );

    return (
        <ProfileEdit
            title="פרטים אישיים"
            onSave={onSave}
            saveDisabled={saveDisabled}
            firstColumnContent={FirstColumnContent}
            secondColumnContent={SecondColumnContent}
        />
    );
}
